import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import TicketMessagesDialog from "../TicketMessagesDialog";

/**
 * Aviso de atención duplicada (0424 F3 · CU-5).
 *
 * Desde que cada canal tiene su propio hilo, la misma persona puede estar siendo
 * atendida en dos conversaciones a la vez. El riesgo no es técnico sino reputacional:
 * dos agentes respondiendo cosas distintas al mismo cliente.
 *
 * Barra discreta, nunca un modal: informa y deja trabajar. Si el contacto solo tiene
 * esta conversación —el caso de casi todo el mundo— no se pinta nada.
 */
const useStyles = makeStyles(theme => ({
  barra: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    fontSize: 13,
    lineHeight: 1.35,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,193,7,0.12)" : "#FFF8E1",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    color: theme.palette.text.primary,
    flexShrink: 0
  },
  icono: { fontSize: 18, opacity: 0.75, flexShrink: 0 },
  texto: { flex: 1, minWidth: 0 },
  enlace: {
    background: "none",
    border: "none",
    padding: 0,
    margin: "0 0 0 4px",
    font: "inherit",
    fontWeight: 600,
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "underline"
  }
}));

const OtherThreadsNotice = ({ ticketId }) => {
  const classes = useStyles();
  const history = useHistory();
  const [hilos, setHilos] = useState([]);
  const [hiloEnLectura, setHiloEnLectura] = useState(null);

  useEffect(() => {
    if (!ticketId) {
      setHilos([]);
      return undefined;
    }
    let vigente = true;
    (async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}/other-threads`);
        if (vigente) setHilos(data?.threads || []);
      } catch (err) {
        // Un aviso no puede romper la ficha: si falla, simplemente no se muestra.
        if (err?.response?.status !== 403) toastError(err);
        if (vigente) setHilos([]);
      }
    })();
    return () => { vigente = false; };
  }, [ticketId]);

  if (hilos.length === 0) return null;

  /**
   * Dos caminos, según lo que autorice el servidor (0424 §7.1.b):
   *
   *   canOpen → es suyo o lo gobierna: se abre entero.
   *   canRead → lo atiende otra persona: se abre en SOLO LECTURA, para tener el
   *             contexto de lo que ya le dijeron al cliente y no responder por encima.
   *
   * Sin ninguno de los dos no hay enlace: el aviso informa y ahí acaba.
   */
  const accionar = hilo => {
    if (hilo.canOpen) history.push(`/tickets/${hilo.uuid}`);
    else if (hilo.canRead) setHiloEnLectura(hilo.id);
  };

  const describir = h => {
    const canal = h.whatsappName || "otra conexión";
    const quien = h.userName
      ? `atendida por ${h.userName}`
      : h.queueName
        ? `en ${h.queueName}, sin agente`
        : "sin agente";
    return `${canal} · ${quien}`;
  };

  /**
   * El enlace solo aparece si el servidor dice que este agente puede abrir ese hilo
   * (`canOpen`). Saber que el contacto habla en otro canal no es poder entrar ahí: un
   * USER ve el aviso —lo necesita para no contradecir a un compañero delante del
   * cliente— pero no el acceso. Un ADMIN o el SUPERVISOR de ese equipo sí.
   *
   * Nunca se pinta un enlace que vaya a devolver un 403.
   */
  const etiquetaDe = h => h.whatsappName || `#${h.id}`;

  return (
    <div className={classes.barra}>
      <InfoOutlinedIcon className={classes.icono} />
      <span className={classes.texto}>
        {hilos.length === 1 ? (
          <>
            Este contacto tiene <strong>otra conversación abierta</strong> en{" "}
            {describir(hilos[0])}
            {(hilos[0].canOpen || hilos[0].canRead) && (
              <button type="button" className={classes.enlace} onClick={() => accionar(hilos[0])}>
                {hilos[0].canOpen ? "abrir" : "ver conversación"}
              </button>
            )}
          </>
        ) : (
          <>
            Este contacto tiene <strong>{hilos.length} conversaciones abiertas</strong> en otros
            canales:{" "}
            {hilos.map((h, i) => (
              <React.Fragment key={h.id}>
                {i > 0 && ", "}
                {h.canOpen || h.canRead ? (
                  <button type="button" className={classes.enlace} onClick={() => accionar(h)}>
                    {etiquetaDe(h)}
                  </button>
                ) : (
                  <span>{etiquetaDe(h)}</span>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </span>

      {/* El mismo diálogo que usa «espiar conversación»: no se inventa nada, ya es de
          solo lectura. Recibe el hilo de origen para que el servidor autorice. */}
      {hiloEnLectura && (
        <TicketMessagesDialog
          open={!!hiloEnLectura}
          handleClose={() => setHiloEnLectura(null)}
          ticketId={hiloEnLectura}
          desdeTicketId={ticketId}
        />
      )}
    </div>
  );
};

OtherThreadsNotice.propTypes = {
  ticketId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default OtherThreadsNotice;
